/**
 * Agent Configuration Types
 *
 * Shared types for all Agent features: config form, autonomy dial,
 * intent preview, audit trail, cost guard.
 */

// -- Enums / Unions --

export type AutonomyLevel = 'conservative' | 'balanced' | 'autonomous'
export type SeverityLevel = 'info' | 'warning' | 'critical'
export type AgentPreset = 'customer_support' | 'content_manager' | 'operations' | 'custom'
export type ActionCategory = 'data_read' | 'data_modify' | 'block_modify' | 'deploy' | 'delete' | 'other'

// -- Agent Role Types (D-02, D-22) --

export type AgentRole = 'operator' | 'support' | 'analytics' | 'custom_1' | 'custom_2'

export const MAX_AGENTS_PER_APP = 5

export const ROLE_PRESET_MAP: Record<AgentRole, AgentPreset | null> = {
  operator: 'operations',
  support: 'customer_support',
  analytics: 'content_manager', // closest existing preset
  custom_1: null,
  custom_2: null,
}

export const AGENT_ROLE_LABELS: Record<AgentRole, { label: string; icon: string }> = {
  operator: { label: 'Operator', icon: 'Wrench' },
  support: { label: 'Support', icon: 'MessageCircle' },
  analytics: { label: 'Analytics', icon: 'BarChart3' },
  custom_1: { label: 'Custom 1', icon: 'Puzzle' },
  custom_2: { label: 'Custom 2', icon: 'Puzzle' },
}

// -- Agent Village Types (D-01, D-02, D-03) --

export type AgentStatus = 'active' | 'idle' | 'error' | 'offline'

export interface AgentVillageStatus {
  app_id: string
  status: AgentStatus
  last_action: string | null   // "product price update"
  last_event_at: string | null // ISO timestamp
}

export interface AgentVillageMultiStatus {
  app_id: string
  agents: Array<{
    agent_role: AgentRole
    status: AgentStatus
    last_action: string | null
    last_event_at: string | null
  }>
}

// -- Interfaces --

export interface CostLimits {
  per_request_tokens: number  // default 4000 (D-26)
  per_task_tokens: number     // default 20000 (D-26)
  per_day_usd: number         // default 20.00 (D-26)
}

export interface AgentConfig {
  id: string
  app_id: string
  agent_role: AgentRole      // D-02: per-role agent config
  persona: string
  preset: AgentPreset
  tools: string[]
  autonomy_level: AutonomyLevel
  cost_limits: CostLimits
  created_at: string
  updated_at: string
  created_by: string | null
  embedded_enabled: boolean  // D-18: default false
}

export interface AgentEvent {
  id: string
  agent_id: string | null
  app_id: string | null
  event_type: string
  severity: SeverityLevel
  action_category: ActionCategory | null
  reversible: boolean
  undo_payload: Record<string, unknown> | null
  duration_ms: number | null
  payload: Record<string, unknown>
  created_at: string
}

// -- Constants --

/**
 * Autonomy x Severity behavior matrix (D-10)
 * Determines how agent actions are presented to the user.
 * - 'auto': execute silently, log only
 * - 'toast': execute + show toast notification (undo available)
 * - 'confirm': show inline confirmation required before execution
 */
export const AUTONOMY_BEHAVIOR: Record<AutonomyLevel, Record<SeverityLevel, 'auto' | 'toast' | 'confirm'>> = {
  conservative: { info: 'toast', warning: 'confirm', critical: 'confirm' },
  balanced:     { info: 'auto',  warning: 'toast',   critical: 'confirm' },
  autonomous:   { info: 'auto',  warning: 'auto',    critical: 'confirm' },
}

export const DEFAULT_COST_LIMITS: CostLimits = {
  per_request_tokens: 4000,
  per_task_tokens: 20000,
  per_day_usd: 20.00,
}

/**
 * 3 presets per D-21: Customer Support, Content Manager, Operations
 */
export const AGENT_PRESETS: Record<Exclude<AgentPreset, 'custom'>, {
  persona: string
  tools: string[]
  autonomy_level: AutonomyLevel
}> = {
  customer_support: {
    persona: 'Handles customer inquiries, order lookups, and returns',
    tools: ['order_lookup', 'refund_process', 'faq_search', 'ticket_create'],
    autonomy_level: 'balanced',
  },
  content_manager: {
    persona: 'Manages product listings, descriptions, and media',
    tools: ['block_edit', 'image_upload', 'content_publish', 'seo_analyze'],
    autonomy_level: 'conservative',
  },
  operations: {
    persona: 'Monitors inventory, processes orders, and generates reports',
    tools: ['inventory_check', 'order_process', 'report_generate', 'alert_manage'],
    autonomy_level: 'autonomous',
  },
}
